import { Icon } from 'components/Icon';
import { render, type JSXChild } from 'engine/index';
import { Hourglass, Minus, Plus, Trophy, X } from 'lucide-static';
import { debounce, lateinit, unreachable } from 'shared/util';
import { rpc, State } from 'engine/rpc';
import { signOut } from 'components/Login';
import { setUserIconColor, showModal, StatBubble } from 'components/AppShell';
import { Game } from './game';
import { Timer } from 'components/Timer';
import { BottomRight } from 'components/BottomRight';
import { BoardPanels, type FlikkenUpdate, type BoardPanel, type FlikkenClientPlayerState } from 'shared/modelTypes/flikken-challenge';

export class Flikken extends Game<'flikken-challenge'> {
	override maxPlayers: number = 4;

	shellScoreEl!: HTMLElement;
	player: FlikkenClientPlayerState = lateinit;

	override getGameName(): string {
		return __`Flikken Challenge`;
	}

	override defineBottomUI(): JSXChild {
		return (
			<>
				<StatBubble color="green" label="Score" value="0" id="score" />
				<div>
					<StatBubble
						onClick={() =>
							showModal(
								<NotesPopup
									notes={this.player?.notes ?? ''}
									onChange={(v) => {
										// console.log(v);
										rpc.sendReflectoraMessage.query({
											sessionId: State.gameId.toString(),
											update: { kind: 'save-notes', playerId: State.playerId, notes: v },
										});
										this.player.notes = v;
									}}
								/>,
							)
						}
						color="yellow"
						label="Notities"
						value=" "
						id="notes-bubble-title"
					/>
				</div>
			</>
		);
	}

	override onShellCreated(view: HTMLElement): void {
		super.onShellCreated(view);

		this.shellScoreEl = view.querySelector('#score')!;
	}

	override handleUpdate(update: FlikkenUpdate): void {
		const sessionId = State.gameId.toString();

		const playerId = State.playerId;

		switch (update.kind) {
			case 'show-login-screen':
				this.showLoginScreen();
				break;
			case 'show-register-screen':
				this.showRegisterScreen();
				break;
			case 'show-choosetheme-screen':
				{
					render(
						this.shellContentEl,
						<ChooseTheme
							timerFrom={update.timerFrom}
							timerTo={update.timerTo}
							onChosen={(theme) => {
								rpc.sendFlikkenMessage.query({ sessionId, update: { kind: 'choose-theme', playerId, theme } });
							}}
						/>,
					);
				}
				break;
			case 'show-roundtheme-screen':
				{
					render(
						this.shellContentEl,
						<ShowTheme
							playerName={update.director}
							theme={update.theme}
							timerFrom={update.timerFrom}
							timerTo={update.timerTo}
							onChoose={() => {
								rpc.sendFlikkenMessage.query({ sessionId, update: { kind: 'next-screen' } });
							}}
						/>,
					);
				}
				break;
			case 'show-choosearchive-screen':
				{
					render(
						this.shellContentEl,
						<ChooseArchive
							available={update.available}
							onChosen={(archive) => {
								rpc.sendFlikkenMessage.query({ sessionId, update: { kind: 'choose-archive', playerId, archive } });
							}}
						/>,
					);
				}
				break;
			case 'show-choosequestion-screen':
				{
					render(
						this.shellContentEl,
						<ChooseQuestionNumber
							numberOptions={update.numberOptions}
							archive={update.archive}
							onChoose={(num) => {
								rpc.sendFlikkenMessage.query({ sessionId, update: { kind: 'choose-question-number', playerId, num } });
							}}
						/>,
					);
				}
				break;
			case 'show-presentquestion-screen':
				{
					render(this.shellContentEl, <PresentQuestion archive={update.archive} question={update.question} />);
				}
				break;
			case 'show-reviewquestion-screen':
				{
					render(
						this.shellContentEl,
						<ReviewQuestion
							archive={update.archive}
							onChoose={(accepted) => {
								rpc.sendFlikkenMessage.query({ sessionId, update: { kind: 'review-question', playerId, accepted } });
							}}
						/>,
					);
				}
				break;

			case 'show-wait-screen':
				render(this.shellContentEl, <WaitForTurn msg={update.msg} playerName={update.playerName} />);
				break;
			case 'show-choosegame-screen':
				{
					render(
						this.shellContentEl,
						<ChooseGame
							timerFrom={update.timerFrom}
							timerTo={update.timerTo}
							onChosen={(game) => {
								rpc.sendFlikkenMessage.query({ sessionId, update: { kind: 'choose-game', playerId, game } });
							}}
						/>,
					);
				}
				break;

			case 'show-presentgame-screen':
				{
					render(
						this.shellContentEl,
						<PresentGame
							game={update.game}
							timerFrom={update.timerFrom}
							timerTo={update.timerTo}
							onChoose={() => {
								rpc.sendFlikkenMessage.query({ sessionId, update: { kind: 'next-screen' } });
							}}
						/>,
					);
				}
				break;

			case 'show-reviewgames-screen':
				{
					render(
						this.shellContentEl,
						<ReviewGames
							games={update.games}
							onReview={(reviewedGames) => {
								rpc.sendFlikkenMessage.query({ sessionId, update: { kind: 'review-games', playerId, reviewedGames } });
							}}
						/>,
					);
				}
				break;

			case 'show-results-screen':
				{
					render(this.shellContentEl, <ResultsScreen results={update.players} />);
				}
				break;
			case 'update-players-state':
				this.updatePlayersState(update);
				break;
			case 'update-player-state':
				State.name = update.player.name;
				State.color = update.player.color;
				State.score = update.player.score;

				this.shellNameEl.innerText = State.name;
				this.shellScoreEl.innerText = State.score.toString();
				this.player = update.player;
				setUserIconColor(this.userIconEl, State.color);

				break;
			case 'trigger-effect':
				this.triggerEffect(update);
				break;
			default:
				unreachable(update);
		}
	}
}

export function ChooseTheme({
	onChosen,
	timerFrom,
	timerTo,
}: {
	timerFrom: number;
	timerTo: number;
	onChosen: (t: { title: string; description: string }) => void;
}) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-8 p-6 md:max-w-screen-md">
			<div class="text-center text-xl text-gray-800">
				Welk thema zou jij graag willen aanpakken? Je gaat jouw medespelers uitdagen om hier een spelvorm voor te bedenken.
			</div>
			<form
				class="flex w-[min(100%,400px)] flex-col gap-5"
				on:submit={(e: SubmitEvent) => {
					e.preventDefault();
					const d = new FormData(e.target as HTMLFormElement);
					onChosen(Object.fromEntries(d.entries()) as { title: string; description: string });
				}}
			>
				<div class="flex flex-col">
					<label class="font-bold" for="theme-title">
						Titel thema
					</label>
					<input
						type="text"
						name="title"
						id="theme-title"
						class="block w-full rounded-md border border-gray-300 bg-gray-50 p-2"
					/>
				</div>
				<div class="flex flex-col">
					<label class="font-bold" for="theme-desc">
						Details
					</label>
					<textarea
						type="text"
						name="description"
						id="theme-desc"
						rows="8"
						class="rounded-md border border-gray-300 bg-gray-50 p-2"
					/>
				</div>
				<button class="rounded-md bg-theme-600 p-4 text-theme-50">Verstuur</button>
			</form>
			<BottomRight>
				<Timer timerFrom={timerFrom} timerTo={timerTo} />
			</BottomRight>
		</div>
	);
}

export function ShowTheme({
	playerName,
	theme,
	timerFrom,
	timerTo,
	onChoose,
}: {
	playerName: string | undefined;
	theme: { title: string; description: string };
	timerFrom: number;
	timerTo: number;
	onChoose: () => void;
}) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-8 p-6 md:max-w-screen-md">
			<div class="text-center text-xl text-gray-800">
				{playerName && `${playerName} is de rechercheur! Zijn/haar thema gaan we speels aanpakken.`}
				{!playerName && `Jij bent de rechercheur! Jouw thema gaan we speels aanpakken.`}
			</div>
			<div class="w-[min(100%,400px)] rounded-md border border-theme-300 bg-theme-50 p-4">
				<h2 class="text-lg font-bold">{theme.title}</h2>
				<p class="mt-2">{theme.description}</p>
			</div>{' '}
			{!playerName && (
				<button class="rounded-md bg-gray-200 p-4 text-gray-900" on:click={onChoose}>
					Start
				</button>
			)}
			<BottomRight>
				<Timer timerFrom={timerFrom} timerTo={timerTo} />
			</BottomRight>
		</div>
	);
}

export function ChooseArchive({ onChosen, available }: { available: number[]; onChosen: (chosen: BoardPanel) => void }) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-8 p-6 lg:max-w-screen-lg">
			<div class="text-center text-xl text-gray-800">
				Jij bent aan zet! Kies een archiefkamer uit waar jij jouw vraag wilt ophalen.
			</div>
			<div class="grid w-full grid-cols-2 gap-4">
				{BoardPanels.map((key, i) => (
					<button
						key={key}
						class="flex aspect-[512/347] w-full items-center justify-center rounded-md bg-cover bg-center"
						style={`background-image: url('/flikken-challenge/${key}.png'); opacity: ${available.includes(i) ? '1' : '.4'}`}
						on:click={() => (available.includes(i) ? onChosen(key) : null)}
					></button>
				))}
			</div>
		</div>
	);
}

export function ChooseQuestionNumber({
	numberOptions,
	archive,
	onChoose,
}: {
	numberOptions: (number | null)[];
	onChoose: (num: number) => void;
	archive: BoardPanel;
}) {
	const order = [1, 4, 6, 2, 7, 3, 5, 8];

	return (
		<div class="flex h-fit w-full shrink-0 flex-col items-center gap-2 p-6 lg:max-w-screen-lg">
			<h2 class="text-2xl font-bold text-gray-900 lg:text-6xl">Jij bent aan zet!</h2>
			<p class="mt-2 w-full text-center font-sans text-gray-800">Welk nummer kies jij uit?</p>
			<div class="mt-8 grid w-full grid-cols-3 gap-1">
				{numberOptions.map((v, i) => {
					return (
						<button
							on:click={() => (v === null ? undefined : onChoose(v))}
							class={`aspect-[2/1] size-full rounded-md border bg-cover bg-center px-1 py-2 text-xl text-theme-950 ${i === 1 ? 'row-span-2' : ''}`}
							style={`background-image: url('/flikken-challenge/${archive}-${order[i]}.png'); opacity: ${v === null ? '0.2' : '1'}`}
						></button>
					);
				})}
			</div>
		</div>
	);
}

export function PresentQuestion({ archive, question }: { archive: string; question: string }) {
	return (
		<div class="flex h-fit w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:text-6xl">Jij bent aan zet!</h2>
			<p class="mt-2 w-full text-center font-sans text-gray-800">
				<b>{_k('flikken.archive.' + archive)}</b> - Stel de vraag aan de rechercheur
			</p>
			<div class="rounded-lg border border-theme-400 bg-theme-100 p-4 text-center text-theme-950">{question}</div>
		</div>
	);
}

export function ReviewQuestion({ archive, onChoose }: { archive: string; onChoose: (yn: boolean) => void }) {
	return (
		<div class="flex h-fit w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:text-6xl">Beoordeel</h2>
			<p class="mt-2 w-full text-center font-sans text-gray-800">
				<b>{_k('flikken.archive.' + archive)}</b> - Was de vraag relevant?
			</p>
			<div class="mt-6 flex w-full flex-row gap-5">
				<button on:click={() => onChoose(true)} class="flex-1 rounded-md bg-theme-600 p-4 text-theme-50">
					Ja
				</button>
				<button on:click={() => onChoose(false)} class="flex-1 rounded-md bg-theme-600 p-4 text-theme-50">
					Nee
				</button>
			</div>
		</div>
	);
}

export function ChooseGame({
	onChosen,
	timerFrom,
	timerTo,
}: {
	timerFrom: number;
	timerTo: number;
	onChosen: (t: { title: string; description: string }) => void;
}) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-8 p-6 md:max-w-screen-md">
			<div class="text-center text-xl text-gray-800">
				De tijd is voorbij. Bedenk op basis van de antwoorden een spelvorm die jij wilt voorleggen aan de rechercheur.
			</div>
			<form
				class="flex w-[min(100%,400px)] flex-col gap-5"
				on:submit={(e: SubmitEvent) => {
					e.preventDefault();
					const d = new FormData(e.target as HTMLFormElement);
					onChosen(Object.fromEntries(d.entries()) as { title: string; description: string });
				}}
			>
				<div class="flex flex-col">
					<label class="font-bold" for="theme-title">
						Titel spel
					</label>
					<input
						type="text"
						name="title"
						id="theme-title"
						class="block w-full rounded-md border border-gray-300 bg-gray-50 p-2"
					/>
				</div>
				<div class="flex flex-col">
					<label class="font-bold" for="theme-desc">
						Details
					</label>
					<textarea
						type="text"
						name="description"
						id="theme-desc"
						rows="8"
						class="rounded-md border border-gray-300 bg-gray-50 p-2"
					/>
				</div>
				<button class="rounded-md bg-theme-600 p-4 text-theme-50">Verstuur</button>
			</form>
			<BottomRight>
				<Timer timerFrom={timerFrom} timerTo={timerTo} />
			</BottomRight>
		</div>
	);
}

export function PresentGame({
	game,
	timerFrom,
	timerTo,
	onChoose,
}: {
	game: { title: string; description: string };
	timerFrom: number;
	timerTo: number;
	onChoose: () => void;
}) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-8 p-6 md:max-w-screen-md">
			<div class="text-center text-xl text-gray-800">Vertel in het kort wat jouw idee is aan de rechercheur.</div>
			<div class="w-[min(100%,400px)] rounded-md border border-theme-300 bg-theme-50 p-4">
				<h2 class="text-lg font-bold">{game.title}</h2>
				<p class="mt-2">{game.description}</p>
			</div>{' '}
			<button class="rounded-md bg-gray-200 p-4 text-gray-900" on:click={onChoose}>
				Klaar
			</button>
			<BottomRight>
				<Timer timerFrom={timerFrom} timerTo={timerTo} />
			</BottomRight>
		</div>
	);
}

export function ReviewGames({ games, onReview }: { games: string[]; onReview: (r: number[]) => void }) {
	const reviewScores = games.map((_) => 0);
	const maxScore = 10;
	const minScore = 0;

	const view: HTMLElement = (
		<div class="flex w-full flex-col items-center justify-center gap-8 p-6 md:max-w-screen-md">
			<div class="text-center text-xl text-gray-800">
				Jij mag als rechercheur 10 punten verdelen. Bepaal welk idee jij het beste vind aansluiten bij jouw thema.
			</div>
			<small>
				Nog <span id="remaining">10</span> punten.
			</small>
			<div class="flex w-full flex-col gap-3">
				{games.map((game, i) => {
					const cnt: HTMLElement = (
						<div class="flex size-10 shrink-0 items-center justify-center rounded-full border border-gray-400 bg-gray-50">
							0
						</div>
					);
					return (
						<div class="flex flex-row items-center gap-1">
							<div class="mr-2 flex-1 shrink rounded-md border border-theme-300 bg-theme-50 p-3">
								<p class="font-semibold">{game}</p>
							</div>

							<button
								on:click={() => {
									if (reviewScores[i] > minScore) {
										reviewScores[i]--;
										cnt.textContent = reviewScores[i].toString();

										const totalScore = reviewScores.reduce((acc, c) => acc + c, 0);
										rem.textContent = (maxScore - totalScore).toString();
									}
								}}
							>
								<Icon icon={Minus} class="size-6" />
							</button>
							{cnt}
							<button
								on:click={() => {
									const totalScore = reviewScores.reduce((acc, c) => acc + c, 0);
									if (totalScore < maxScore) {
										reviewScores[i]++;
										cnt.textContent = reviewScores[i].toString();

										rem.textContent = (maxScore - (totalScore + 1)).toString();
									}
								}}
								class="group-data-[maxxed]:opacity-50"
							>
								<Icon icon={Plus} class="size-6" />
							</button>
						</div>
					);
				})}
			</div>
			<button
				class="rounded-md bg-theme-600 p-4 text-theme-50"
				on:click={(_e: Event) => {
					onReview(reviewScores);
				}}
			>
				Verstuur
			</button>
		</div>
	);

	const rem = view.querySelector('#remaining') as HTMLElement;

	return view;
}

export function WaitForTurn({ msg, playerName }: { msg?: string; playerName?: string }) {
	return (
		<div class="flex flex-col items-center justify-center gap-2 p-6 md:max-w-screen-md">
			<Icon icon={Hourglass} class="size-20 text-gray-300" />
			<div class="text-center text-xl text-gray-800">
				{playerName && (
					<>
						<span class="font-bold text-gray-900">{playerName}</span> {__`teamKleuren.waitForTurn.action`}
					</>
				)}
				{!playerName && msg}
			</div>
		</div>
	);
}

export function ResultsScreen({ results }: { results: { name: string; color: string; score: number }[] }) {
	return (
		<div class="flex w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">Einduitslag</h2>
			<table class="overflow-hidden rounded-md shadow-sm">
				<thead>
					<tr class="bg-gray-100">
						<th class="px-5 py-1">Rank</th>
						<th class="px-5 py-1">Pion</th>
						<th class="px-5 py-1">Naam</th>
						<th class="px-5 py-1">Punten</th>
					</tr>
				</thead>
				<tbody>
					{results.map((r, i) => {
						const highlight = State.color == r.color;

						return (
							<tr class={highlight ? 'bg-yellow-100 ring-2 ring-yellow-400' : 'even:bg-gray-50'}>
								<td class="px-5 py-1">
									<div class="inline-flex items-baseline gap-3">
										<span class="font-bold">{i + 1}</span>
										{i == 0 && <Icon icon={Trophy} class="size-4 text-orange-400" />}
										{i == 1 && <Icon icon={Trophy} class="size-4 text-gray-400" />}
										{i == 2 && <Icon icon={Trophy} class="size-4 text-orange-700" />}
										{i >= 3 && <Icon icon={Trophy} class="size-4 opacity-0" />}
									</div>
								</td>
								<td class="px-5 py-1">{r.color}</td>
								<td class="px-5 py-1">{r.name}</td>
								<td class="px-5 py-1">{r.score}</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			<button class="rounded-md bg-gray-200 p-4 text-gray-900" on:click={signOut}>
				Spel afsluiten
			</button>
		</div>
	);
}

export function NotesPopup({ notes, onChange }: { notes: string; onChange: (v: string) => void }) {
	const debouncedCallback = debounce(onChange, 500);
	return (
		<div class="relative flex max-h-full w-full max-w-md flex-col items-center rounded-lg border border-gray-100 bg-white p-4 shadow-lg">
			<button action-cancel class="absolute right-2 top-2 rounded-md p-2">
				<Icon icon={X} class="size-6" />
			</button>
			<h3 class="mb mb-6 text-lg font-bold text-gray-900">Aantekeningen</h3>
			<textarea
				on:input={(e: InputEvent) => debouncedCallback((e.target as HTMLTextAreaElement).value)}
				rows="5"
				class="w-full rounded-md border border-gray-400 bg-gray-50 p-1"
			>
				{notes}
			</textarea>
		</div>
	);
}
